import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { parseDateInTz } from '@/utils/timezone';
import { shiftDay, type ScheduleJob } from '@/components/schedule/scheduleModel';

// No money columns: CSRs and other restricted roles dispatch too.
const SCHEDULE_SELECT = `
  id, job_number, status, scheduled_date, scheduled_time, scheduled_time_start, scheduled_time_end,
  assigned_tech_id, customer_name, customer_address, customer_suburb, customer_postcode,
  issue_description, lat, lng, metro_id,
  tech_ack_status, tech_ack_at, tech_ack_reset_at, tech_decline_reason,
  en_route_at, en_route_eta_minutes, en_route_eta_at, arrived_at, work_finished_at, hold_reason,
  service_types(name),
  techs(id, name)
` as const;

/**
 * Jobs booked from `firstDay` to `lastDay` inclusive ("yyyy-MM-dd" in the company timezone),
 * cancelled ones left out. Keyed under ['jobs', ...] so RealtimeProvider refreshes it.
 */
export function useScheduleJobs(firstDay: string, lastDay: string, timezone: string) {
  const from = parseDateInTz(firstDay, timezone).toISOString();
  const to = parseDateInTz(shiftDay(lastDay, 1), timezone).toISOString();

  return useQuery({
    queryKey: ['jobs', 'schedule', from, to],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select(SCHEDULE_SELECT)
        .gte('scheduled_date', from)
        .lt('scheduled_date', to)
        .neq('status', 'cancelled')
        .order('scheduled_time_start', { ascending: true, nullsFirst: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as unknown as ScheduleJob[];
    },
    placeholderData: keepPreviousData,
  });
}

export interface ScheduleMoveInput {
  jobId: string;
  jobNumber: string | null;
  day: string;
  start: string | null;
  end: string | null;
  techId: string | null;
  techName: string | null;
}

/** office_schedule_job (20260924160000): day, window and technician in one transaction. */
export function useScheduleMove() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (move: ScheduleMoveInput) => {
      const { error } = await supabase.rpc('office_schedule_job', {
        p_job_id: move.jobId,
        p_date: move.day,
        p_time_start: move.start ?? undefined,
        p_time_end: move.end ?? undefined,
        p_tech_id: move.techId ?? undefined,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_, move) => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', move.jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-audit-log', move.jobId] });
      toast({
        title: `${move.jobNumber ?? 'Job'} moved`,
        description: move.techName
          ? `${move.techName} will be asked to accept it in the Steddy app.`
          : 'It is now unassigned.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Could not move the job',
        description: error.message.replace(/^STATE_CONFLICT:\s*/, ''),
        variant: 'destructive',
      });
    },
  });
}

export interface AcceptedEstimateToBook {
  id: string;
  estimate_number: string;
  estimate_amount: number;
  customer_name: string | null;
  accepted_at: string | null;
  deposit_amount: number | null;
  deposit_paid_amount: number;
  lead_id: string;
  lead: { id: string; lead_number: string | null; status: string | null } | null;
}

export interface ToBook {
  /** Accepted estimate, lead not booked yet: book the job from the lead. */
  leads: AcceptedEstimateToBook[];
  /** Tech quoted on site, customer accepted: drag the job onto a lane for the return visit. */
  jobs: { job: ScheduleJob; estimate: AcceptedEstimateToBook }[];
}

/**
 * Work the customer has said yes to that has no appointment yet (migration 20260924170000:
 * accepted estimates move their lead to estimate_accepted; on-site quotes park the job at
 * quote_sent). Estimates keyed under ['estimates'], jobs under ['jobs'], so realtime refreshes both.
 */
export function useToBook() {
  const accepted = useQuery({
    queryKey: ['estimates', 'to-book'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('estimates')
        .select(`id, estimate_number, estimate_amount, customer_name, accepted_at, deposit_amount, deposit_paid_amount, lead_id,
                 lead:leads(id, lead_number, status)`)
        .eq('status', 'accepted')
        .order('accepted_at', { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as unknown as AcceptedEstimateToBook[];
    },
  });

  const parked = useQuery({
    queryKey: ['jobs', 'schedule', 'quote-sent'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select(SCHEDULE_SELECT + ', lead_id')
        .eq('status', 'quote_sent')
        .limit(300);
      if (error) throw error;
      return (data ?? []) as unknown as (ScheduleJob & { lead_id: string })[];
    },
  });

  const estimates = accepted.data ?? [];
  const byLead = new Map<string, AcceptedEstimateToBook>();
  for (const e of estimates) if (!byLead.has(e.lead_id)) byLead.set(e.lead_id, e);

  const data: ToBook = {
    leads: [...byLead.values()].filter((e) => e.lead?.status === 'estimate_accepted'),
    jobs: (parked.data ?? [])
      .filter((j) => byLead.has(j.lead_id))
      .map((job) => ({ job, estimate: byLead.get(job.lead_id) as AcceptedEstimateToBook })),
  };
  return { data, isLoading: accepted.isLoading || parked.isLoading };
}
